import React, { useState, useEffect, useContext, Fragment, useRef } from 'react';
import Resizer from "react-image-file-resizer";
import { APPConstants } from '../../../../../constants/app';
import { ProjectContext } from '../../../../../context/Project';
import { blobImageExtension } from '../../../../Utilities/Blob';
import {
	Container,
	Row,
	Col,
	Input,
	Button
} from "reactstrap";
import '../steps.scss';
import CameraSnapIcon from "../../../assets/camera-snap.png";
import LightBulbIcon from "../../../assets/light-bulb.png";
import PictureIcon from '../../../assets/picture-icon.svg';
import useBom from '../../../../../hooks/useBom';
import useImageGeneration from '../../../../../hooks/useImageGeneration';
import Alert from '../../../../Form/Alert';
import useProjectSteps from '../../../../../hooks/useProjectSteps';
import useBlobStorage from '../../../../../hooks/useBlobStorage';
import Header from '../Header';
import Loader from './loader';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import useLogin from '../../../../../hooks/useLogin';
import { PixelContext } from '../../../../../context/Pixel';

function UploadPhotoStep() {
	const [selectedFile, setSelectedFile] = useState(null);
	const [prompt, setPrompt] = useState('');
	const [showImageError, setShowImageError] = useState(false);
	const [imageError, setImageError] = useState("We couldn't process the uploaded image. Plase try again");

	const bom = useBom();
	const login = useLogin();
	const blobStorage = useBlobStorage();
	const uploadBlobStorage = useBlobStorage();
	const projectSteps = useProjectSteps();
	const promptInputRef = useRef(null);
	const imageGeneration = useImageGeneration();
	const isAuthenticated = useIsAuthenticated();
	const { inProgress } = useMsal();
	const { setArea, setDescription, setType, setGeneratedAiData, setSpacePhotos, project } = useContext(ProjectContext);
	const { trackAnalyticsEvent, Events } = useContext(PixelContext);

	const handleSubmit = (event) => {
		event.preventDefault();
	}

	const handlePromptChange = (e) => {
		setPrompt(e.target.value);
	};

	useEffect(() => {
		let spacePhotos = project?.spacePhotos || [];
		let projectPrompt = project?.description;

		if (!selectedFile && !prompt && spacePhotos.length > 0 && projectPrompt && inProgress === InteractionStatus.None && isAuthenticated) {

			createFileFromUrl(spacePhotos[0].url, spacePhotos[0].name)
				.then(file => {
					setSelectedFile({ file: file, preview: getPreview(file), generated: true });
  				});

			
			setPrompt(projectPrompt);
		}
		
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inProgress]);

	useEffect(() => {
		if (selectedFile && !prompt) {
			promptInputRef.current.focus();
		}

		if (selectedFile?.generated && prompt) {
			generateAiImage();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedFile]);

	useEffect(() => {
		if (project?.generatedAiData?.imageUrl && project?.generatedAiData?.bom && Array.isArray(project?.spacePhotos)) {
			projectSteps.goNextStep();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [project]);

	useEffect(() => {
		if (imageGeneration.generatedImage) {
			bom.generateBom(selectedFile.file, prompt);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [imageGeneration.generatedImage]);

	useEffect(() => {
		if (bom.generatedBom) {
			setArea(bom.generatedBom.floorArea);
			setType({id: bom.generatedBom.projectTypeId});
			setDescription(prompt);

			if (selectedFile?.file && imageGeneration?.generatedImage) {
				const aiGeneratedImage = imageGeneration.generatedImage;
				aiGeneratedImage.name = getAiGeneratedImageName();
				blobStorage.uploadFormFilesBlob([aiGeneratedImage]);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bom.generatedBom]);

	useEffect(() => {
		if (blobStorage.uploadedFiles?.length > 0) {
			const spacePhotos = [];
			blobStorage.uploadedFiles.forEach((uploadedFile) => {
				let imageName = getAiGeneratedImageName();
				spacePhotos.push({
					uploadedFile: uploadedFile,
                    url: blobStorage.getUploadedFileUrl(uploadedFile),
					name: imageName
				})
			});

			setSpacePhotos(spacePhotos);
			const aiGeneratedImageUrl = spacePhotos[spacePhotos.length - 1]?.url
			setGeneratedAiData(getAiGeneratedImageName(), aiGeneratedImageUrl, bom.generatedBom);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [blobStorage.uploadedFiles]);

	useEffect(() => {
		if (blobStorage.error || imageGeneration.error || bom.error) {
			if (imageGeneration.error?.detail) {
				setImageError(imageGeneration.error.detail);
				setShowImageError(true);
			}

			if (blobStorage.error?.detail) {
				setImageError(blobStorage.error?.detail);
				setShowImageError(true);
			}

			if (uploadBlobStorage.error?.detail) {
				setImageError(uploadBlobStorage.error?.detail);
				setShowImageError(true);
			}

			if (bom.error) {
				setImageError("Something went wrong generating the material list. Please try again later.");
				setShowImageError(true);
			}
		}
	}, [blobStorage.error, imageGeneration.error, bom.error, uploadBlobStorage.error]);

	const selectFiles = (event) => {
		const selectedFile = event.target.files[0];

		if (selectedFile) {
			const uploadPhotoConstants = APPConstants.PROJECT.STEPS.UPLOAD_PHOTO;

			Resizer.imageFileResizer(
				selectedFile,
				uploadPhotoConstants.UPLOAD_REZIZE_MAX_WIDTH,
				uploadPhotoConstants.UPLOAD_RESIZE_MAX_HEIGHT,
				uploadPhotoConstants.UPLOAD_RESIZE_FORMAT,
				uploadPhotoConstants.UPLOAD_RESIZE_QUALITY,
				0,
				(file) => {
					if (file) {
						setSelectedFile({ file: file, preview: getPreview(file) });
						trackAnalyticsEvent(Events.UPLOAD_PHOTO_CHOSEN);
					} else {
						setSelectedFile(null);
						setImageError("Something went wrong resizing the image. Please try again later.");
						setShowImageError(true);
					}
				},
				uploadPhotoConstants.UPLOAD_RESIZE_OUTPUT_TYPE
			)
		} else {
			setSelectedFile(null);
		}
	};

	const getPreview = (file) => {
		let image = null;

		if (file) {
			image = {
				blob: URL.createObjectURL(file),
				name: file.name,
				size: file.size
			}

		}

		return image;
	}

	async function createFileFromUrl(fileUrl, fileName) {
  		const response = await fetch(fileUrl);
  		const dataBlob = await response.blob();
  		const fileMetadata = { type: dataBlob.type };
  		return new File([dataBlob], fileName, fileMetadata);
	}

	const generateAiImage = () => {
		if (selectedFile?.file && prompt) {
			imageGeneration.generateImage(selectedFile.file, prompt);
		}
	}

	const processUploadedFile = () => {
		if (selectedFile?.file && prompt) {
			// Upload selected file
			uploadBlobStorage.uploadFormFilesBlob([selectedFile.file]);
		}
	}

	useEffect(() => {
		if (uploadBlobStorage.uploadedFiles?.length > 0) {
			const spacePhotos = [];
			uploadBlobStorage.uploadedFiles.forEach((uploadedFile) => {
				let imageName = selectedFile?.file?.name;
				spacePhotos.push({
					uploadedFile: uploadedFile,
                    url: uploadBlobStorage.getUploadedFileUrl(uploadedFile),
					name: imageName
				})
			});

			setSpacePhotos(spacePhotos);
			setDescription(prompt);

			if (inProgress === InteractionStatus.None) {
				if (isAuthenticated) {
					generateAiImage();
				} else {
					login.redirectToLogin(window.location.pathname);
				}
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [uploadBlobStorage.uploadedFiles]);

	const getAiGeneratedImageName = () => {
		return imageGeneration.generatedImage ? `ai_generated_space.${blobImageExtension(imageGeneration.generatedImage)}` : null;
	}

	return (
		<Fragment>
			<Header title="Step 1: Upload Photo" progress={12}/>
			<form className="m-t-20 upload-photo-form" onSubmit={handleSubmit}>
				<Container className="builde-cont">
					<Row>
						<Col xs={{ size: 12 }}>
							<div className='rounded-gray-container'>
								<Row>
									<Col xs="12">
										<Row>
											<Col xs="6" className='text-center border-right'>
												<img className='w-50 p-3' src={CameraSnapIcon} alt="" />
											</Col>
											<Col xs="6" className='text-center'>
												<img className='w-50 p-3' src={LightBulbIcon} alt="" />
											</Col>
										</Row>
										<Row>
											<Col xs="6" className='text-center border-right'>
												<p className='font-noto-serif display-6 fw-bold'>Click to Upload</p>
											</Col>
											<Col xs="6" className='text-center'>
												<p className='font-noto-serif display-6 fw-bold'>Prompt</p>
											</Col>
										</Row>
										<Row>
											<Col xs="6" className='text-center align-content-center border-right'>
												{
													!selectedFile?.preview?.blob ?
														<span className='font-helvetica-world'>
															File Formats: JPG, PNG
														</span>
														:
														<img alt="" className='mw-100' src={selectedFile.preview.blob}
															onError={(e) => { e.target.src = PictureIcon }} />
												}
											</Col>
											<Col xs="6" className='text-center prompt-example'>
												<Row>
													<Col className='gx-0'>
														<span className='font-helvetica-world'>
															<Input
																className='prompt-background border-0'
																type="textarea"
																rows="7"
																name="prompt"
																value={prompt}
																innerRef={promptInputRef}
																onChange={handlePromptChange}
																placeholder="Describe what you’d like to renovate - e.g. ‘modern kitchen
																	with more storage’ or ‘small bathroom makeover with white tiles.’"
															/>
														</span>
													</Col>
												</Row>
											</Col>
										</Row>
										<Row>
											<Col xs="6" className='text-center border-right mt-1'>
												<Input
													id="upload-image-ai"
													name="upload_image"
													type="file"
													multiple={false}
													accept=".jpg, .jpeg, .png"
													onChange={selectFiles}
													className='d-none'
												/>
												<label className='upload-image-ai-label upload-button-graient px-4 py-2 text-light float-none'
													htmlFor="upload-image-ai">
														Choose File
												</label>
											</Col>
											<Col xs="6" />
										</Row>

									</Col>
								</Row>
							</div>
						</Col>
					</Row>
					<Row>
						<Col sm="12" className='text-center'>
							<small className='fw-bold font-helvetica-world'>Pro tip:</small>
						</Col>
					</Row>
					<Row>
						<Col sm="12" className='text-center'>
							<small className='font-helvetica-world'>Select a well lit photo for best results.</small>
						</Col>
					</Row>
					<Row>
						<Col xs={{ size: 12 }}>

						</Col>
					</Row>
				</Container>
			</form>
			<footer className={'position-fixed bottom-0 w-100 h-3'}>
				<Container className='my-3'>
					<Row>
						<Col xs="12" className='text-center'>
							<div className="d-grid gap-2">
								<Button
									color="primary"
									size="lg"
									disabled={!selectedFile || !prompt}
									onClick={() => {
										trackAnalyticsEvent(Events.UPLOAD_PHOTO_CONTINUE_CLICK, {
											prompt: prompt
										});
										processUploadedFile();
									}}
								>Continue</Button>
							</div>
						</Col>
					</Row>
				</Container>
			</footer>
			<Loader active={imageGeneration.loading || bom.loading || blobStorage.loading}
				loaderText={'Analyzing your space and generating ideas… This may take up to 2 minutes.'}/>
			<Loader active={uploadBlobStorage.loading}
				loaderText={'Analizing image, please wait...'}/>
			<Alert message={imageError}
				title={'Image processing problem'}
				show={showImageError ? 1 : 0}
				onClose={() => setShowImageError(!showImageError)}
			/>
		</Fragment>
	);
}

export default UploadPhotoStep;